import { TaxonomyIntent, GraphData } from '@/hooks/useTaxonomyRun'

// ── Colour helpers (copied from EntityGraph — keeps them independent) ──
const PALETTE = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#14b8a6']
function hashString(s: string): number {
  let hash = 0
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash)
  return Math.abs(hash)
}
function getColour(intentName: string): string {
  return PALETTE[hashString(intentName) % PALETTE.length]
}

// ── Pure logic — exported for testing ────────────────────────────
export interface ComputedStats {
  totalIntents:     number
  totalTags:        number
  totalEntityTypes: number
  totalRelations:   number
  sorted:           TaxonomyIntent[]   // by document_count desc
  topEdges:         GraphData['edges'] // top 6 by weight desc
}

export function computeStats(intents: TaxonomyIntent[], graphData: GraphData): ComputedStats {
  const totalIntents     = intents.length
  const totalTags        = intents.reduce((sum, i) => sum + i.tags.length, 0)
  const totalEntityTypes = intents.reduce((sum, i) => sum + i.entity_types.length, 0)
  const totalRelations   = graphData.edges.length
  const sorted           = [...intents].sort((a, b) => b.document_count - a.document_count)
  const topEdges         = [...graphData.edges].sort((a, b) => b.weight - a.weight).slice(0, 6)

  return { totalIntents, totalTags, totalEntityTypes, totalRelations, sorted, topEdges }
}

interface StatsViewProps {
  intents:   TaxonomyIntent[]
  graphData: GraphData
}

export function StatsView({ intents, graphData }: StatsViewProps) {
  const { totalIntents, totalTags, totalEntityTypes, totalRelations, sorted, topEdges } =
    computeStats(intents, graphData)
  const maxDocCount = sorted.length > 0 ? sorted[0].document_count : 1

  return (
    <div className="mt-2 space-y-6">

      {/* ── Row 1: Summary cards (4 across) ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Intents',      value: totalIntents,     colour: 'blue' },
          { label: 'Tags',         value: totalTags,        colour: 'green' },
          { label: 'Entity Types', value: totalEntityTypes, colour: 'purple' },
          { label: 'Relations',    value: totalRelations,   colour: 'amber' },
        ].map(card => (
          <div key={card.label} className="border rounded-lg bg-white p-4 shadow-sm">
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{card.label}</p>
          </div>
        ))}
      </div>

      {/* ── Row 2: Intent Distribution ── */}
      <div className="border rounded-lg bg-white p-4 shadow-sm">
        <p className="text-sm font-semibold text-gray-700 mb-3">Intent Distribution</p>
        <div className="space-y-2.5">
          {sorted.map(intent => {
            const barWidth = (intent.document_count / maxDocCount) * 100
            const colour   = getColour(intent.name)
            return (
              <div key={intent.id} className="flex items-center gap-3">
                {/* Intent name label — fixed width, truncated */}
                <span className="text-xs text-gray-600 w-36 shrink-0 truncate text-right" title={intent.name}>
                  {intent.name}
                </span>
                {/* Bar track */}
                <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${barWidth}%`, backgroundColor: colour }}
                  />
                </div>
                {/* Count label */}
                <span className="text-xs font-medium text-gray-500 w-8 shrink-0">
                  {intent.document_count}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Row 3: Strongest Relations table ── */}
      <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
        <div className="p-4 border-b">
          <p className="text-sm font-semibold text-gray-700">Strongest Relations</p>
        </div>

        {topEdges.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No relationships to display.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase tracking-wide bg-gray-50">
                <th className="px-4 py-2 font-medium">Entity A</th>
                <th className="px-4 py-2 font-medium">Entity B</th>
                <th className="px-4 py-2 font-medium">Weight</th>
              </tr>
            </thead>
            <tbody>
              {topEdges.map((edge, idx) => (
                <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                  {/* Entity A — blue badge */}
                  <td className="px-4 py-2.5">
                    <span className="inline-block font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200">
                      {edge.source}
                    </span>
                  </td>
                  {/* Entity B — green badge */}
                  <td className="px-4 py-2.5">
                    <span className="inline-block font-mono text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded border border-green-200">
                      {edge.target}
                    </span>
                  </td>
                  {/* Weight — mini progress bar + number */}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${edge.weight * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 font-medium">{edge.weight.toFixed(2)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
